import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { ArrowUp, Square, Paperclip, X } from "lucide-react";
import type { SlashCommand } from "../types";
import { SlashCommandMenu } from "./SlashCommandMenu";
import { ContextMenu, type ContextItem, type ContextMenuHandle } from "./ContextMenu";

/** Stored mention: display name → metadata mapping */
interface MentionEntry {
  displayName: string;
  metadata: Record<string, unknown>;
}

interface ChatInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  onFilesSelected?: (files: File[]) => void;
  disabled?: boolean;
  slashCommands?: SlashCommand[];
  pendingFiles?: File[];
  onRemoveFile?: (index: number) => void;
  uploadingFiles?: boolean;
  uploadedFileNames?: string[];
}

function escapeRegex(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function ChatInput({
  onSend,
  onStop,
  onFilesSelected,
  disabled = false,
  slashCommands = [],
  pendingFiles = [],
  onRemoveFile,
  uploadingFiles = false,
  uploadedFileNames = [],
}: ChatInputProps) {
  const [input, setInput] = useState("");
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextQuery, setContextQuery] = useState("");
  const [atTriggerPos, setAtTriggerPos] = useState(-1);
  const [mentions, setMentions] = useState<MentionEntry[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const contextMenuRef = useRef<ContextMenuHandle>(null);

  // Auto-resize textarea + sync mirror height
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const h = Math.min(el.scrollHeight, 120);
    el.style.height = `${h}px`;
    if (mirrorRef.current) {
      mirrorRef.current.style.height = `${h}px`;
    }
  }, [input]);

  // Sync scroll between textarea and mirror
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const onScroll = () => {
      if (mirrorRef.current) {
        mirrorRef.current.scrollTop = el.scrollTop;
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      const cursorPos = e.target.selectionStart ?? value.length;
      setInput(value);

      // Clean up mentions that are no longer in the text
      setMentions((prev) =>
        prev.filter((m) => value.includes(`@${m.displayName}`))
      );

      // Slash menu detection
      setShowSlashMenu(value.startsWith("/") && slashCommands.length > 0 && !showContextMenu);

      // @ context menu detection
      const textBeforeCursor = value.substring(0, cursorPos);
      const lastAtIndex = textBeforeCursor.lastIndexOf("@");

      if (lastAtIndex >= 0) {
        const charBefore = lastAtIndex > 0 ? value[lastAtIndex - 1] : " ";
        if (charBefore === " " || charBefore === "\n" || lastAtIndex === 0) {
          const queryAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
          // Check this isn't an existing mention
          const isExistingMention = mentions.some(
            (m) => queryAfterAt.startsWith(m.displayName)
          );
          if (!isExistingMention && !queryAfterAt.includes("\n")) {
            setShowContextMenu(true);
            setContextQuery(queryAfterAt);
            setAtTriggerPos(lastAtIndex);
            return;
          }
        }
      }

      if (showContextMenu) {
        setShowContextMenu(false);
        setContextQuery("");
        setAtTriggerPos(-1);
      }
    },
    [slashCommands.length, showContextMenu, mentions]
  );

  const handleContextSelect = useCallback(
    (item: ContextItem) => {
      // Insert clean @DisplayName (no brackets/metadata)
      const mention = `@${item.displayName} `;
      const before = input.substring(0, atTriggerPos);
      const cursorPos = textareaRef.current?.selectionStart ?? input.length;
      const after = input.substring(cursorPos);
      setInput(before + mention + after);

      // Track the mention for later expansion on send
      setMentions((prev) => {
        // Avoid duplicates
        if (prev.some((m) => m.displayName === item.displayName)) return prev;
        return [...prev, { displayName: item.displayName, metadata: item.metadata }];
      });

      setShowContextMenu(false);
      setContextQuery("");
      setAtTriggerPos(-1);
      textareaRef.current?.focus();
    },
    [input, atTriggerPos]
  );

  const handleContextClose = useCallback(() => {
    setShowContextMenu(false);
    setContextQuery("");
    setAtTriggerPos(-1);
    textareaRef.current?.focus();
  }, []);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || disabled) return;

    // Expand mentions: @DisplayName → @[DisplayName](metadata)
    let expanded = trimmed;
    for (const m of mentions) {
      const metaStr = JSON.stringify(m.metadata);
      expanded = expanded.replace(
        `@${m.displayName}`,
        `@[${m.displayName}](${metaStr})`
      );
    }

    onSend(expanded);
    setInput("");
    setMentions([]);
    setShowSlashMenu(false);
    setShowContextMenu(false);
    textareaRef.current?.focus();
  }, [input, disabled, onSend, mentions]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Forward navigation keys to ContextMenu when open
      if (showContextMenu) {
        if (["ArrowUp", "ArrowDown", "Tab", "Escape"].includes(e.key) ||
            (e.key === "Enter" && !e.shiftKey)) {
          contextMenuRef.current?.handleKeyDown(e);
          return;
        }
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
      if (e.key === "Escape") {
        setShowSlashMenu(false);
      }
    },
    [handleSend, showContextMenu]
  );

  const handleSlashSelect = useCallback(
    (cmd: SlashCommand) => {
      setInput(`${cmd.name} `);
      setShowSlashMenu(false);
      textareaRef.current?.focus();
    },
    []
  );

  const handleFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0 && onFilesSelected) {
        onFilesSelected(Array.from(files));
      }
      e.target.value = "";
    },
    [onFilesSelected]
  );

  // Render mirror text with badge-styled mentions
  const mirrorContent = useMemo(() => {
    if (mentions.length === 0) {
      // No mentions — render plain (transparent text matches textarea exactly)
      return <span>{input || "\u00A0"}</span>;
    }

    // Build regex to find all @DisplayName occurrences
    const sortedNames = [...mentions]
      .map((m) => m.displayName)
      .sort((a, b) => b.length - a.length);
    const pattern = sortedNames.map((n) => `@${escapeRegex(n)}`).join("|");
    const regex = new RegExp(`(${pattern})`, "g");

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let key = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(input)) !== null) {
      // Text before mention
      if (match.index > lastIndex) {
        parts.push(
          <span key={key++} className="text-text-primary">
            {input.slice(lastIndex, match.index)}
          </span>
        );
      }
      // Badge
      parts.push(
        <span
          key={key++}
          className="bg-accent-violet/20 text-accent-violet rounded-sm text-[13px] leading-relaxed font-sans"
        >
          {match[0]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    // Remaining text
    if (lastIndex < input.length) {
      parts.push(
        <span key={key++} className="text-text-primary">
          {input.slice(lastIndex)}
        </span>
      );
    }

    return <>{parts.length > 0 ? parts : <span>{"\u00A0"}</span>}</>;
  }, [input, mentions]);

  const hasInput = input.trim().length > 0;
  const hasMentions = mentions.length > 0;

  return (
    <>
      {showSlashMenu && !showContextMenu && (
        <SlashCommandMenu
          commands={slashCommands}
          filter={input.slice(1)}
          onSelect={handleSlashSelect}
        />
      )}
      <div className="px-3 pb-3 pt-1 shrink-0 relative z-[1]">
        {/* Context Menu */}
        {showContextMenu && (
          <ContextMenu
            ref={contextMenuRef}
            query={contextQuery}
            onSelect={handleContextSelect}
            onClose={handleContextClose}
            uploadedFiles={uploadedFileNames}
          />
        )}

        {/* Pending files preview */}
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2 px-1">
            {pendingFiles.map((file, i) => (
              <div
                key={`${file.name}-${i}`}
                className="flex items-center gap-1.5 bg-surface-raised border border-border-subtle rounded-lg px-2.5 py-1.5 text-[11px] text-text-secondary max-w-[160px]"
              >
                <Paperclip size={10} className="shrink-0 text-text-muted" />
                <span className="truncate">{file.name}</span>
                {onRemoveFile && !uploadingFiles && (
                  <button
                    onClick={() => onRemoveFile(i)}
                    className="shrink-0 text-text-muted hover:text-text-primary transition-colors"
                    aria-label={`Remove ${file.name}`}
                  >
                    <X size={10} />
                  </button>
                )}
                {uploadingFiles && (
                  <span className="shrink-0 text-accent-violet text-[9px]">uploading...</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 cai-input-ring bg-surface-raised rounded-2xl px-3.5 py-2.5">
            {onFilesSelected && (
              <>
                <button
                  onClick={handleFileClick}
                  disabled={disabled || uploadingFiles}
                  aria-label="Attach files"
                  className="w-6 h-6 rounded-full flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-surface-glass transition-colors shrink-0 disabled:opacity-30"
                >
                  <Paperclip size={14} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json,.md"
                />
              </>
            )}
            <div className="flex-1 relative">
              {/* Mirror overlay for badge rendering */}
              {hasMentions && (
                <div
                  ref={mirrorRef}
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none whitespace-pre-wrap break-words overflow-hidden text-[13px] leading-relaxed font-sans p-0"
                  style={{ minHeight: "22px", padding: 0, wordBreak: "break-word" }}
                >
                  {mirrorContent}
                </div>
              )}
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={disabled ? "Waiting for response..." : "Ask CAi anything... (@ for context)"}
                disabled={disabled && !onStop}
                rows={1}
                aria-label="Chat message input"
                className={`w-full bg-transparent text-[13px] resize-none focus:outline-none disabled:opacity-40 placeholder:text-text-muted max-h-[120px] leading-relaxed font-sans p-0 ${
                  hasMentions ? "text-transparent caret-text-primary" : "text-text-primary"
                }`}
                style={{ minHeight: "22px", padding: 0 }}
              />
            </div>
            {disabled && onStop ? (
              <button
                onClick={onStop}
                aria-label="Stop generation"
                className="w-8 h-8 bg-accent-violet/20 rounded-full flex items-center justify-center text-accent-violet hover:bg-accent-violet/30 transition-colors shrink-0"
              >
                <Square size={10} fill="currentColor" />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!hasInput || disabled}
                aria-label="Send message"
                className="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0 cai-glow-btn"
              >
                <ArrowUp size={14} strokeWidth={2.5} />
              </button>
            )}
        </div>
      </div>
    </>
  );
}
